import { z } from "zod";
import { paginatedSchema, paginationQuerySchema } from "../common/pagination.js";
import { idSchema, isoDateTimeSchema, userSummarySchema } from "../common/primitives.js";
import { workspaceRoleSchema } from "../workspaces/roles.js";
import { workspaceParamsSchema } from "../workspaces/workspace.schemas.js";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const PROJECT_STATUSES = ["PLANNED", "ACTIVE", "ON_HOLD", "COMPLETED", "CANCELLED"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const projectStatusSchema = z.enum(PROJECT_STATUSES);

export const PROJECT_STATUS_LABELS: Readonly<Record<ProjectStatus, string>> = {
  PLANNED: "Planned",
  ACTIVE: "Active",
  ON_HOLD: "On hold",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

/** Project keys prefix task identifiers (`ENG-123`), so they are short and uppercase. */
export const PROJECT_KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** Calendar date without a time zone, `YYYY-MM-DD`. */
export const isoDateSchema = z.iso.date();

export const projectSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: projectStatusSchema,
  owner: userSummarySchema.nullable(),
  memberCount: z.number().int().nonnegative(),
  teamCount: z.number().int().nonnegative(),
  startDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Project = z.infer<typeof projectSchema>;

export const projectMemberSchema = z.object({
  userId: idSchema,
  workspaceRole: workspaceRoleSchema,
  joinedAt: isoDateTimeSchema,
  user: userSummarySchema,
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const projectKeySchema = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(
    z
      .string()
      .min(2, "Key must be at least 2 characters")
      .max(10, "Key must be at most 10 characters")
      .regex(PROJECT_KEY_PATTERN, "Start with a letter; use only letters and numbers"),
  );

const projectNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(80, "Name must be at most 80 characters");
const projectDescriptionSchema = z
  .string()
  .trim()
  .max(2000, "Description must be at most 2000 characters");

/** Both dates set → the due date cannot be before the start date. Shared with tasks. */
export function datesInOrder(value: {
  startDate?: string | null | undefined;
  dueDate?: string | null | undefined;
}): boolean {
  return !value.startDate || !value.dueDate || value.startDate <= value.dueDate;
}
export const DATE_ORDER_ERROR = {
  error: "Due date must be on or after the start date",
  path: ["dueDate"],
};

export const MAX_PROJECT_MEMBERS_PER_REQUEST = 100;

export const createProjectSchema = z
  .object({
    name: projectNameSchema,
    key: projectKeySchema,
    description: projectDescriptionSchema.optional(),
    status: projectStatusSchema.optional(),
    ownerId: idSchema.nullable().optional(),
    /** Workspace members added to the project; the owner is always included. */
    memberIds: z.array(idSchema).max(MAX_PROJECT_MEMBERS_PER_REQUEST).optional(),
    startDate: isoDateSchema.nullable().optional(),
    dueDate: isoDateSchema.nullable().optional(),
  })
  .refine(datesInOrder, DATE_ORDER_ERROR);
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = z
  .object({
    name: projectNameSchema.optional(),
    key: projectKeySchema.optional(),
    description: projectDescriptionSchema.nullable().optional(),
    status: projectStatusSchema.optional(),
    ownerId: idSchema.nullable().optional(),
    startDate: isoDateSchema.nullable().optional(),
    dueDate: isoDateSchema.nullable().optional(),
  })
  .refine((value) => Object.values(value).some((v) => v !== undefined), {
    error: "Provide at least one field to update",
  })
  .refine(datesInOrder, DATE_ORDER_ERROR);
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

export const listProjectsQuerySchema = paginationQuerySchema.extend({
  status: projectStatusSchema.optional(),
  /** `true` returns only the projects the caller is a member of. */
  mine: z.enum(["true", "false"]).optional(),
});
export type ListProjectsQuery = z.infer<typeof listProjectsQuerySchema>;
export interface ListProjectsQueryInput {
  limit?: number | undefined;
  cursor?: string | undefined;
  status?: ProjectStatus | undefined;
  mine?: boolean | undefined;
}

export const projectParamsSchema = workspaceParamsSchema.extend({ projectId: idSchema });
export const addProjectMemberSchema = z.object({ userId: idSchema });
export type AddProjectMemberInput = z.infer<typeof addProjectMemberSchema>;
export const projectMemberParamsSchema = projectParamsSchema.extend({ userId: idSchema });

export const projectKeyParamsSchema = workspaceParamsSchema.extend({
  projectKey: projectKeySchema,
});

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const projectsResponseSchema = paginatedSchema(projectSchema);
export type ProjectsResponse = z.infer<typeof projectsResponseSchema>;

export const projectResponseSchema = z.object({ project: projectSchema });
export type ProjectResponse = z.infer<typeof projectResponseSchema>;

export const projectMembersResponseSchema = z.object({ members: z.array(projectMemberSchema) });
export type ProjectMembersResponse = z.infer<typeof projectMembersResponseSchema>;
